import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import { useAppStore } from '../stores/appStore'
import { setPageMeta } from '../composables/useSeoMeta'
import { releaseGlobalWakeLock } from '../composables/useWakeLock'
import { resetModalBackHandlerOnRouteNavigation } from '../composables/useModalBackHandler'
import { recordAppNavigation } from '../composables/useAppNavigation'
import { isChunkLoadError, handleChunkLoadFailure } from '../utils/resilience'

import PageMain from '../views/PageMain.vue'
const PageRecipe = () => import('../views/PageRecipe.vue')
const PageArticles = () => import('../views/PageArticles.vue')
const PageArticleCategories = () => import('../views/PageArticleCategories.vue')
const PageRecipeCategories = () => import('../views/PageRecipeCategories.vue')
const PageUnits = () => import('../views/PageUnits.vue')
const PageSettings = () => import('../views/PageSettings.vue')
const PageTrash = () => import('../views/PageTrash.vue')
const PageLogin = () => import('../views/PageLogin.vue')
const PageSupport = () => import('../views/PageSupport.vue')
const PageIndex = () => import('../views/PageIndex.vue')
const PagePrivacy = () => import('../views/PagePrivacy.vue')
const PageNotFound = () => import('../views/PageNotFound.vue')

export const TWO_HOURS_MS = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

export const resolveStartRoute = (store: ReturnType<typeof useAppStore>): string => {
  const startPageSetting = store.startPage || 'shopping-list-if-unchecked'
  if (startPageSetting === 'shopping-list') {
    return '/shopping-list'
  }
  if (startPageSetting === 'recipes') {
    return '/recipes'
  }
  if (startPageSetting === 'shopping-list-if-unchecked') {
    const hasUncheckedItems = store.shoppingList && store.shoppingList.some(
      i => !i.checked && i.articleID !== '__LIST__' && i.articleID !== '__INVITATION__'
    )
    return hasUncheckedItems ? '/shopping-list' : '/recipes'
  }
  return '/recipes'
}

export const getRedirectForRoot = (): string => {
  const authStore = useAuthStore()
  // Unauthenticated guests navigating to "/" directly land on Kochbuch (Demo Mode)
  if (!authStore.user) {
    return '/recipes'
  }

  const store = useAppStore()
  const startPageSetting = store.startPage || 'shopping-list-if-unchecked'
  const lastUsed = localStorage.getItem('last_used_route')
  const lastActiveRaw = localStorage.getItem('last_active_time')
  const lastActiveTime = lastActiveRaw ? parseInt(lastActiveRaw, 10) : 0
  const now = Date.now()
  const isInactiveOver2Hours = !lastActiveTime || (now - lastActiveTime > TWO_HOURS_MS)

  // 1. If user explicitly configured 'last-used', always restore last used route if valid
  if (startPageSetting === 'last-used' && lastUsed && lastUsed !== '/' && lastUsed !== '/login') {
    return lastUsed
  }

  // 2. If app was active within the last 2 hours, restore last used route
  if (!isInactiveOver2Hours && lastUsed && lastUsed !== '/' && lastUsed !== '/login') {
    return lastUsed
  }

  // 3. Otherwise (> 2h inactive or startPage setting should take precedence): apply configured start page
  return resolveStartRoute(store)
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: () => getRedirectForRoot() },
  { path: '/login', name: 'login', component: PageLogin, meta: { title: 'Login', requiresAuth: false } },
  { path: '/user', name: 'user', component: () => import('../views/PageUser.vue'), meta: { title: 'Mein Profil', requiresAuth: true } },
  { path: '/recipes', name: 'recipes', component: PageMain, meta: { title: 'Kochbuch', requiresAuth: false } },
  { path: '/recipe/:id', name: 'recipe', component: PageRecipe, meta: { title: 'Rezept', requiresAuth: false } },
  { path: '/recipe/:id/print', name: 'recipe-print', component: () => import('../views/PageViewRecipePrint.vue'), meta: { title: 'Rezept drucken', requiresAuth: false, isPrint: true } },
  { path: '/shopping-list', name: 'shopping-list', component: PageMain, meta: { title: 'Einkaufsliste', requiresAuth: false } },
  { path: '/articles', name: 'articles', component: PageArticles, meta: { title: 'Artikel', requiresAuth: false } },
  { path: '/article-categories', alias: '/categories', name: 'article-categories', component: PageArticleCategories, meta: { title: 'Artikel-Kategorien', requiresAuth: false } },
  { path: '/recipe-categories', name: 'recipe-categories', component: PageRecipeCategories, meta: { title: 'Rezept-Kategorien', requiresAuth: false } },
  { path: '/units', name: 'units', component: PageUnits, meta: { title: 'Einheiten', requiresAuth: false } },
  { path: '/settings', name: 'settings', component: PageSettings, meta: { title: 'Einstellungen', requiresAuth: false } },
  { path: '/trash', name: 'trash', component: PageTrash, meta: { title: 'Papierkorb', requiresAuth: false } },
  { path: '/support', name: 'support', component: PageSupport, meta: { title: 'Unterstützen', requiresAuth: false } },
  { path: '/privacy', alias: '/datenschutz', name: 'privacy', component: PagePrivacy, meta: { title: 'Datenschutz', requiresAuth: false } },
  { path: '/index', name: 'index', component: PageIndex, meta: { title: 'Index', requiresAuth: true, requiresAdmin: true } },
  {
    path: '/pair/:code?',
    name: 'pair',
    redirect: (to) => {
      const code = (to.params as any).code || to.query.code
      return {
        path: '/settings',
        query: {
          tab: 'system',
          watchPair: '1',
          ...(code ? { code: String(code) } : {})
        }
      }
    }
  },
  {
    path: '/notifications',
    name: 'notifications',
    redirect: () => {
      const store = useAppStore()
      store.isNotificationsModalOpen = true
      return getRedirectForRoot()
    }
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: PageNotFound, meta: { title: '404 – Nicht gefunden', requiresAuth: false } }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (
      (to.path === '/recipes' && from.path === '/shopping-list') ||
      (to.path === '/shopping-list' && from.path === '/recipes')
    ) {
      return false
    }
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

router.beforeEach(async (to, _from, next) => {
  resetModalBackHandlerOnRouteNavigation()
  const authStore = useAuthStore()
  
  const requiresAuthEvaluation = Boolean(
    to.meta.requiresAdmin ||
    to.meta.requiresAuth ||
    to.path === '/index' ||
    to.name === 'index' ||
    to.path === '/admin' ||
    to.name === 'admin' ||
    to.name === 'login'
  )

  // Only wait for auth initialization if this route actually depends on auth status!
  // Public routes (like /recipes, /shopping-list, /articles, /support) mount immediately without waiting!
  if (requiresAuthEvaluation && !authStore.isAuthReady) {
    try {
      await authStore.initializeAuth()
    } catch {}
  }

  // Stealth guard for /index: if not logged in or not admin, return 404 (not-found) to avoid exposing route existence
  if (to.meta.requiresAdmin || to.path === '/index' || to.name === 'index' || to.path === '/admin' || to.name === 'admin') {
    if (!authStore.user || !authStore.isAdmin) {
      next({ name: 'not-found', replace: true })
      return
    }
  }

  if (to.meta.requiresAuth && !authStore.user) {
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && authStore.user) {
    next({ name: 'recipes' })
  } else {
    next()
  }
})

router.afterEach((to, from) => {
  recordAppNavigation(to.fullPath, from?.fullPath)
  resetModalBackHandlerOnRouteNavigation()

  // Release WakeLock and cancel any recipe screen timers when navigating away from recipe view
  if (to.name !== 'recipe' && !to.path.startsWith('/recipe/')) {
    releaseGlobalWakeLock()
  }

  if (to.meta.title) {
    setPageMeta({ title: to.meta.title as string })
  }

  const authStore = useAuthStore()
  if (authStore.user && to.path !== '/login' && to.path !== '/' && to.name !== 'not-found' && to.name !== 'admin') {
    // For articles page, don't persist category/unit query params in last_used_route so app reopens on "Alle"
    const routeToSave = to.path === '/articles' ? '/articles' : (to.fullPath || to.path)
    localStorage.setItem('last_used_route', routeToSave)
    localStorage.setItem('last_active_time', Date.now().toString())
  }

  const baseName = 'Kochbuch & Einkaufsliste'

  // Dynamic document title update (skip 'recipe' since RecipeDetailContent manages custom recipe title)
  if (to.name !== 'recipe') {
    const titleText = (to.meta.title as string) || ''
    document.title = titleText ? `${titleText} – ${baseName}` : `${baseName} – Rezepte verwalten & gemeinsam einkaufen`
  }

  const fullUrl = window.location.origin + to.path

  // Dynamic canonical link update
  const canonicalEl = document.getElementById('canonical-link') as HTMLLinkElement | null
  if (canonicalEl) {
    canonicalEl.href = fullUrl
  }

  // Dynamic og:url update
  const ogUrlEl = document.getElementById('og-url')
  if (ogUrlEl) {
    ogUrlEl.setAttribute('content', fullUrl)
  }

  // Dynamic robots meta: noindex for login-only pages, index for public pages
  const robotsEl = document.getElementById('meta-robots') as HTMLMetaElement | null
  if (robotsEl) {
    const publicRoutes = ['recipes', 'recipe', 'recipe-print', 'shopping-list', 'articles', 'article-categories', 'recipe-categories', 'support', 'privacy', 'login']
    const isPublic = publicRoutes.includes(to.name as string)
    robotsEl.content = isPublic
      ? 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
      : 'noindex, nofollow'
  }
})

router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    console.warn('[Router] Chunk loading failed (likely due to a new deployment). Reloading page...', error)
    handleChunkLoadFailure(to?.fullPath)
  } else {
    console.error('[Router] Unhandled navigation error:', error)
  }
})

export default router
